import type { Draft } from "@reduxjs/toolkit";
import type { BaseQueryFn, EndpointBuilder } from "@reduxjs/toolkit/query";
import {
  EventStreamHttpError,
  fetchEventStream,
} from "@repo/ui/api/eventStream";
import { checkIsAbortError } from "@repo/ui/helpers/errors";
import { handleSessionExpiredResponse } from "./sessionExpiry.js";
import { apiBaseUrl, getApiAuthorizationHeaderValue } from "./shared.js";

type MaybeDrafted<T> = T | Draft<T>;

interface StreamQueryProps<TState, TParams> {
  paramsToPath: (params: TParams) => string;
  paramsToBody: (params: TParams) => unknown;
  initialState: TState;
  updateState: (state: MaybeDrafted<TState>, chunk: unknown) => TState;
  keepUnusedDataFor?: number;
}

interface StreamQueryData<TState> {
  queryCacheKey: string;
  state: TState;
  hasCompleted: boolean;
  /**
   * Set when the stream failed (`state` then holds whatever arrived before the
   * failure, if anything). The query itself never enters RTK Query's error
   * state — `queryFn` resolves immediately and the stream is consumed in
   * `onQueryStarted` — so consumers have to read this to tell a failed stream
   * apart from one that has not produced anything yet.
   */
  hasFailed: boolean;
}

export function streamQuery<TState, TParams>(
  builder: EndpointBuilder<BaseQueryFn, any, "api">,
  props: StreamQueryProps<TState, TParams>,
) {
  const { paramsToPath, paramsToBody } = props;
  const { initialState, updateState, keepUnusedDataFor } = props;

  const abortControllers = new Map<string, AbortController>();

  return builder.query<StreamQueryData<TState>, TParams>({
    queryFn: async (args, { queryCacheKey }) => {
      if (!queryCacheKey) {
        throw new Error("Expected queryCacheKey in queryFn");
      }

      // Abort any existing requests with the same cache key to avoid multiple concurrent streams for the same query args.
      abortControllers.get(queryCacheKey)?.abort();

      // Create a new abort controller for this request.
      const abortController = new AbortController();
      abortControllers.set(queryCacheKey, abortController);

      // Generate the initial state, which will be filled in by onQueryStarted.
      return {
        data: {
          queryCacheKey,
          state: initialState,
          hasCompleted: false,
          hasFailed: false,
        },
      };
    },
    async onQueryStarted(params, { queryFulfilled, updateCachedData }) {
      const {
        data: { queryCacheKey },
      } = await queryFulfilled;
      const abortController = abortControllers.get(queryCacheKey);
      try {
        const path = paramsToPath(params);
        const body = paramsToBody(params);

        const authorization = await getApiAuthorizationHeaderValue();
        abortController?.signal.throwIfAborted();

        const url = new URL(path, apiBaseUrl);
        const events = fetchEventStream(url, {
          headers: {
            authorization,
            "x-alpharun-commit-sha": __COMMIT_SHA__,
          },
          body,
          signal: abortController?.signal,
        });

        for await (const { event, data } of events) {
          if (event !== "message") {
            continue;
          }

          const chunk: unknown = JSON.parse(data);
          updateCachedData((data) => {
            data.state = updateState(data.state, chunk);
          });
        }

        updateCachedData((data) => {
          data.hasCompleted = true;
        });
      } catch (error) {
        if (checkIsAbortError(error)) {
          return;
        }
        if (error instanceof EventStreamHttpError) {
          handleSessionExpiredResponse(error.status, error.body);
        }

        updateCachedData((data) => {
          data.hasFailed = true;
        });

        throw error;
      } finally {
        abortControllers.delete(queryCacheKey);
      }
    },
    async onCacheEntryAdded(args, { cacheDataLoaded, cacheEntryRemoved }) {
      const {
        data: { queryCacheKey },
      } = await cacheDataLoaded;

      // Abort any existing requests with this cache key, in case it's still going. (This happens when a component unmounts and/or cache expires.)
      await cacheEntryRemoved;
      const abortController = abortControllers.get(queryCacheKey);
      abortController?.abort();
      abortControllers.delete(queryCacheKey);
    },
    keepUnusedDataFor,
  });
}
